import type { QuizMaterialResponse } from '../models/quiz';
import type { Highlight } from '../models/selections';
import { isLegacyCfi } from './cfi';
import { componentName, type ComponentNames } from './locator';
import {
  currentPageIndex,
  pageIndexAt,
  type AnchorLocator,
  type AnchorPlacement,
  type PaginationGeometry,
} from './readium';

/** What the reader is showing right now, as far as placing questions goes. */
export interface CurrentPage {
  /** File names, without folder or extension, of the resources on screen. */
  componentIds: string[];
  /** Resolves the idrefs the previous NubeReader saved as `componentId` to file names. */
  componentNames?: ComponentNames;
  fixedLayout: boolean;
  /** Pagination and scroll offset of the chapter frame on screen; undefined while it is not laid out. */
  geometry: PaginationGeometry | undefined;
  anchors: AnchorLocator;
}

type AnchorResolver = () => AnchorPlacement;

const LAST_PROGRESSION = 1;

// Text and CFI anchors in priority order. Each one decides only when it is found in the chapter:
// the quote saved with the question, then the creator's Highlight sharing its id, then the CFI
// the previous NubeReader stored in `selection.percent`.
function anchorResolvers(
  quiz: QuizMaterialResponse,
  highlight: Highlight | undefined,
  anchors: AnchorLocator,
): AnchorResolver[] {
  const { selection } = quiz;
  const resolvers: AnchorResolver[] = [];
  const { textAnchor } = selection;
  const ownQuote = textAnchor?.highlight;
  if (ownQuote?.trim()) {
    resolvers.push(() =>
      anchors.quote({ highlight: ownQuote, before: textAnchor?.before, after: textAnchor?.after }),
    );
  }
  const highlightText = highlight?.locator.text;
  const highlightQuote = highlightText?.highlight;
  if (highlightQuote?.trim()) {
    resolvers.push(() =>
      anchors.quote({
        highlight: highlightQuote,
        before: highlightText?.before,
        after: highlightText?.after,
      }),
    );
  }
  const percent = selection.percent ?? '';
  if (isLegacyCfi(percent)) resolvers.push(() => anchors.cfi(percent));
  return resolvers;
}

/**
 * Progression a question was saved at, as used to place it: missing, blank or non-numeric values
 * (chapter-level questions, unresolved legacy CFIs) mean the chapter's last page.
 */
export function storedProgression(percent: string | undefined): number {
  const progression = percent?.trim() ? Number(percent) : Number.NaN;
  return Number.isFinite(progression) ? progression : LAST_PROGRESSION;
}

/**
 * Whether `quiz` belongs on the page on screen. Every question of a chapter lands on exactly one
 * of its pages whatever the window size, column count or font settings: its text or CFI anchor
 * wins when the chapter contains it, then its creation-time progression mapped onto the current
 * pagination, then the chapter's last page.
 */
export function isQuizOnPage(
  quiz: QuizMaterialResponse,
  highlight: Highlight | undefined,
  page: CurrentPage,
): boolean {
  const { selection } = quiz;
  if (!page.componentIds.includes(componentName(selection.componentId, page.componentNames))) {
    return false;
  }
  if (page.fixedLayout) return true;

  for (const resolve of anchorResolvers(quiz, highlight, page.anchors)) {
    const placement = resolve();
    if (placement !== 'missing') return placement === 'onPage';
  }

  if (!page.geometry) return false;
  return (
    pageIndexAt(page.geometry, storedProgression(selection.percent)) ===
    currentPageIndex(page.geometry)
  );
}
